import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { ChangableComponent } from '@app/models/changable.component';
import { BookmarkComponent } from '@core/components/bookmark/bookmark.component';
import { AccountService, BillingV2Service, ConfigService, FilesService } from '@core/services';
import { Store } from '@ngrx/store';
import { IconComponent } from '@shared/icon/icon.component';
import { RatingComponent } from '@shared/rating/rating.component';
import { loadFilteredLanguages, loadSubjects } from '@store/actions/data.actions';
import {
  IAccountStats,
  ImageSizeEnum,
  Language,
  MediaFormatEnum,
  ProfileMediaModeEnum,
  Subject,
  User,
  UserProfile,
} from 'lingo2-models';
import { first as _first } from 'lodash-es';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { combineLatest, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-tutors',
  standalone: true,
  imports: [LocaleSharedModule, BookmarkComponent, CommonModule, OnUiCoverModule, RatingComponent, IconComponent],
  templateUrl: './tutors.component.html',
  styleUrls: ['./tutors.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TutorsComponent extends ChangableComponent implements OnInit, OnDestroy {
  private configService = inject(ConfigService);
  private filesService = inject(FilesService);
  private billingV2Service = inject(BillingV2Service);
  private sanitizer = inject(DomSanitizer);
  private store = inject(Store);
  deviceService = inject(DeviceDetectorService);

  @Input() view: string;
  @Input() public set user(user: User) {
    this._user = user;
    this.init();
  }

  public get user() {
    return this._user;
  }

  @Output() public openInModal = new EventEmitter<User>();

  public avatarImgUrl: string;
  public countryFlagUrl: string;
  public countryName: string;
  public fullName: string;
  public accountRoute: string[];
  public stats: Partial<IAccountStats>;
  public teachingSubjects: string[] = [];
  public speakLanguages: Language[] = [];
  public about: string;
  public createdAt: Date | string;
  public currentDate = new Date();
  public userPic: OnUiCover;
  public isTeacher: boolean;
  public doHover = false;
  public profileVerified: boolean;
  public mediaUrl: SafeResourceUrl;

  private _user: User;
  private _userProfile: UserProfile;
  private _subjects: Subject[];
  private _languages: Language[];

  ngOnInit(): void {
    combineLatest([this.configService.subjects$, this.configService.languages$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([subjects, languages]) => {
          this._subjects = subjects;
          this._languages = languages;
          this.init();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadSubjects({ locale: this._user.ui_language, force: true }));
          this.store.dispatch(loadFilteredLanguages({ locale: this._user.ui_language }));
        },
      });
  }

  private init() {
    if (!this._user || !this._subjects) {
      return;
    }
    this._userProfile = this._user.profile as UserProfile;
    this.isTeacher = AccountService.isAsIfTeacher(this._user);
    this.profileVerified = this._user?.teacher_profile_verified;
    this.about = this._userProfile?.about;
    this.teachingSubjects = (this._userProfile?.teaching_subjects || [])
      .filter((ts) => ts.subject_id > 0)
      .map((subject) => {
        const _subject = this._subjects.find((s) => s.id === +subject.subject_id);
        return (_subject ? _subject?.title : null) || '---';
      });
    this.speakLanguages = this._userProfile?.spoken_languages
      .sort((a, b) => +a.level_id - +b.level_id)
      .map((_sl) => this._languages.find((_lang) => +_lang.id === +_sl.language_id));
    this.avatarImgUrl =
      this.prepareStorageUrl(this.user?.__opts?.images[0]?.original) ||
      FilesService.getFileUrlBySize(this._user.userpic_id, ImageSizeEnum.original);
    this.countryFlagUrl = this._user?.country?.flag_url;
    this.countryName = this._user?.country?.code;
    this.fullName = AccountService.getUserFullName(this._user);
    this.accountRoute = AccountService.accountRoute(this._user);
    this.userPic = {
      type: 'avatar',
      img: this.avatarImgUrl,
      title: this.fullName,
      form: 'base',
    };

    const teachingSubject = _first(this._userProfile.teaching_subjects);
    if (teachingSubject?.video_mode === ProfileMediaModeEnum.use_media_id && teachingSubject?.video_id) {
      this.filesService
        .getFileById(teachingSubject.video_id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (file) => {
            this.mediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
              file.media.find((_file) => _file.format === MediaFormatEnum.mp4)?.url + '#t=0.001',
            );
            this.detectChanges();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-FILE');
          },
        });
    }
    this.stats = this._user.stats;
    this.createdAt = this._user.created_at;
    this.detectChanges();
  }

  public getFormattedAmount(): Observable<string> {
    return this.billingV2Service.formatAmount$(this.stats.min_price_tier);
  }

  public onOpenInModal() {
    this.openInModal.emit(this._user);
  }

  public favoriteChanged(event) {
    this._user.visit_info.is_favorite = event;
  }

  public trackByFn(index) {
    return index;
  }
}
